import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  TriggerResponseDto,
  WorkTypeResponseDto,
} from '@evimo/client/models';
import {
  formatTriggerSelectLabel,
  resolvePartLabels,
} from '~/app/components/maintenance/tasks-triggers/utils';
import { usePlanAssets } from '~/app/hooks/maintenance/usePlanAssets';
import { useParts } from '~/app/hooks/maintenance/useParts';
import { usePlanDetail } from '~/app/hooks/maintenance/usePlans';
import { usePlanTasks } from '~/app/hooks/maintenance/usePlanTasks';
import { useSchedules } from '~/app/hooks/maintenance/useSchedules';
import { useTasks } from '~/app/hooks/maintenance/useTasks';
import { useTriggers } from '~/app/hooks/maintenance/useTriggers';
import { useWorkTypes } from '~/app/hooks/maintenance/useWorkTypes';
import { PLAN_DETAIL_TABLE_PAGE_SIZE } from '~/app/services/preventive-maintenance/preventive-maintenance.service';
import type { TaskResponseDto } from '@evimo/client/models';
import type { PlanAssetRow, PlanScheduleRow, PlanTaskRow } from '../types';
import { usePlanDetailAssetColumns } from './usePlanDetailAssetColumns';
import { usePlanDetailScheduleColumns } from './usePlanDetailScheduleColumns';
import { usePlanDetailTaskColumns } from './usePlanDetailTaskColumns';

export function usePlanDetailPage() {
  const { t } = useTranslation('maintenance');
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const [assetsPage, setAssetsPage] = useState(1);
  const [schedulesPage, setSchedulesPage] = useState(1);

  const {
    plan,
    isLoading: isPlanLoading,
    error: planError,
  } = usePlanDetail(planId);
  const {
    planTasks,
    isLoading: isTasksLoading,
    error: tasksError,
  } = usePlanTasks(planId);
  const {
    planAssets,
    meta: assetsMeta,
    isFetching: isAssetsFetching,
    error: assetsError,
  } = usePlanAssets(planId, {
    page: assetsPage,
    limit: PLAN_DETAIL_TABLE_PAGE_SIZE,
  });
  const {
    schedules,
    meta: schedulesMeta,
    isFetching: isSchedulesFetching,
    error: schedulesError,
  } = useSchedules(planId, {
    page: schedulesPage,
    limit: PLAN_DETAIL_TABLE_PAGE_SIZE,
  });
  const { data: tasks = [], isLoading: isTaskLibraryLoading } = useTasks();
  const { data: triggers = [], isLoading: isTriggersLoading } = useTriggers();
  const { data: workTypes = [], isLoading: isWorkTypesLoading } =
    useWorkTypes();
  const { parts, isLoading: isPartsLoading } = useParts();

  const { taskColumns, taskExpandable } = usePlanDetailTaskColumns();
  const assetColumns = usePlanDetailAssetColumns();
  const scheduleColumns = usePlanDetailScheduleColumns();

  const workTypeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const wt of workTypes as WorkTypeResponseDto[]) {
      map.set(wt.id, t(`workType.${wt.code}`));
    }
    return map;
  }, [workTypes, t]);

  const taskById = useMemo(() => {
    const map = new Map<string, TaskResponseDto>();
    for (const task of tasks as TaskResponseDto[]) {
      map.set(task.id, task);
    }
    return map;
  }, [tasks]);

  const partNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const part of parts) {
      map.set(part.id, part.name);
    }
    return map;
  }, [parts]);

  const triggerLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const trigger of triggers as TriggerResponseDto[]) {
      map.set(
        trigger.id,
        formatTriggerSelectLabel(trigger.basis, trigger.conditions ?? []),
      );
    }
    return map;
  }, [triggers]);

  const taskRows: PlanTaskRow[] = useMemo(
    () =>
      [...planTasks]
        .sort((a, b) => a.sequence - b.sequence)
        .map((row) => {
          const task = taskById.get(row.taskId);
          return {
            id: row.id,
            sequence: row.sequence,
            taskName: task?.name ?? row.taskId,
            workTypeLabel: task
              ? (workTypeLabelById.get(task.workTypeId) ?? '')
              : '',
            parts: resolvePartLabels(task?.partIds, partNameById),
            subtaskNames: (task?.subtasks ?? []).map((s) => s.name),
            triggerLabel: triggerLabelById.get(row.triggerId) ?? row.triggerId,
          };
        }),
    [planTasks, taskById, workTypeLabelById, partNameById, triggerLabelById],
  );

  const assetRows: PlanAssetRow[] = useMemo(
    () =>
      planAssets.map((row) => ({
        id: row.id,
        assetName: row.assetName,
        serialNumber: row.serialNumber ?? null,
        siteName: row.siteName ?? null,
        active: row.active,
        startDate: String(row.startDate),
        assignedAt: String(row.assignedAt),
      })),
    [planAssets],
  );

  const scheduleRows: PlanScheduleRow[] = useMemo(
    () =>
      schedules.map((row) => ({
        id: row.id,
        assetName: row.assetName,
        serialNumber: row.serialNumber ?? null,
        triggerLabel: triggerLabelById.get(row.triggerId) ?? row.triggerId,
        active: row.active,
        nextDueAt: row.nextDueAt ? String(row.nextDueAt) : null,
        nextDueReading: row.nextDueReading ?? null,
      })),
    [schedules, triggerLabelById],
  );

  // Full-page spinner only for the plan shell / catalogs.
  // Assets & schedules paginate in-place (table loading indicator only).
  const isLoading =
    isPlanLoading ||
    isTasksLoading ||
    isTaskLibraryLoading ||
    isTriggersLoading ||
    isWorkTypesLoading ||
    isPartsLoading;

  const sectionError =
    tasksError?.message ??
    assetsError?.message ??
    schedulesError?.message ??
    null;

  return {
    planId,
    plan,
    isLoading,
    planError,
    sectionError,
    taskRows,
    assetRows,
    scheduleRows,
    taskColumns,
    taskExpandable,
    assetColumns,
    scheduleColumns,
    assetsTotal: assetsMeta.total,
    assetsPage,
    assetsPageSize: PLAN_DETAIL_TABLE_PAGE_SIZE,
    assetsLoading: isAssetsFetching,
    onAssetsPageChange: setAssetsPage,
    schedulesTotal: schedulesMeta.total,
    schedulesPage,
    schedulesPageSize: PLAN_DETAIL_TABLE_PAGE_SIZE,
    schedulesLoading: isSchedulesFetching,
    onSchedulesPageChange: setSchedulesPage,
    onBack: () => navigate('/maintenance/preventive'),
  };
}
